import {
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarContainer,
  GridColDef,
} from "@mui/x-data-grid";
import {
  Button,
  Popover,
  Paper,
  Box,
  Divider,
  Slider,
  Typography,
} from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import React, { FC, ReactElement, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { AppContext } from "../models/applicationState";
import { Context } from "./context";
import dateFormatter from "../util/dateFormatter";
import { CustomExport } from "./customExport";
import { ToolbarToggles } from "./toolbarToggles";
import type { ToolbarTogglesProps } from "./toolbarToggles";

// Re-export for backward compatibility
export { ToolbarToggles } from "./toolbarToggles";
export type { ToolbarTogglesProps } from "./toolbarToggles";
export { ToggleButton } from "./toolbarToggles";
export { ToolbarSettings } from "./toolbarSettings";
export type { ToolbarSettingsProps } from "./toolbarSettings";

interface CustomToolbarProps extends ToolbarTogglesProps {
  showSettings: boolean;
  interval?: number;
  updateInterval?: (newInterval: number) => void;
  updateColumns?: GridColDef[];
}

const CustomToolbar: FC<CustomToolbarProps> = (
  props: CustomToolbarProps,
): ReactElement => {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const appContext = useContext<AppContext>(Context);

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpen(true);
    setAnchorEl(event.currentTarget);
  };

  const changeProgressInterval = (event: Event, selection: number) => {
    props.updateInterval(selection);
    window.localStorage.setItem("interval", `${selection}`);
  };

  const marks = [
    {
      value: 1,
      label: `1 ${t("settings.day")}`,
    },
    {
      value: 90,
      label: `90 ${t("settings.days")}`,
    },
  ];

  const style = {
    width: 500,
    bgcolor: "#fff",
    p: 4,
  };

  const id = open ? "simple-popover" : undefined;
  const todayStr = new Date().toISOString().slice(0, 10);

  // Naming convention for the export file name and print header
  const toolbarNavLinks = [
    { url: "/meetings", name: "menu.create_meeting_note" },
    { url: "/library", name: "menu.meeting_minutes_library" },
    { url: "/dashboard/actor", name: "menu.actor_dashboard" },
    { url: "/dashboard/creator", name: "menu.creator_dashboard" },
    { url: "/events", name: "menu.my_calendar_events" },
    { url: "/tags/all", name: "menu.my_tags" },
  ];

  const matchedNav = toolbarNavLinks.find((l) =>
    location.pathname.startsWith(l.url),
  );
  const navLabel = matchedNav ? t(matchedNav.name) : undefined;

  const contextNameRaw = /^(\/library\/[^/]+|\/meetings\/[^/]+)/.test(
    location.pathname,
  )
    ? appContext.state.selectedList?.name ||
      appContext.state.selectedEvent?.name
    : navLabel ||
      location.pathname
        .replace(/\/+$/, "")
        .split("/")
        .filter(Boolean)
        .join("_");

  const humanTitle = (contextNameRaw || "Export")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const fileSafeTitle = humanTitle
    .replace(/[\\/:*?"<>|]/g, "")
    .trim()
    .slice(0, 80);

  const exportFileName = `Pinnacle ${fileSafeTitle} ${todayStr}`;

  const isMeetingPage = /^(\/library\/[^/]+|\/meetings\/[^/]+)/.test(
    location.pathname,
  );
  const meetingTitle = appContext.state.selectedList?.name || humanTitle;
  const meetingDate = appContext.state.selectedList?.date
    ? dateFormatter.format(new Date(appContext.state.selectedList.date))
    : "";
  const lastEdited = appContext.state.selectedList?.updatedDate
    ? dateFormatter.format(new Date(appContext.state.selectedList.updatedDate))
    : "";
  const invitedList = (appContext.state.selectedList?.participants || []).join(
    ", ",
  );
  const distributionList = (appContext.state.selectedList?.viewers || []).join(
    ", ",
  );

  const customLines = isMeetingPage
    ? [
        `"Pinnacle";"${t("meeting.date")}: ${meetingDate}";"";"";"${t(
          "details.last_edited",
        )}: ${lastEdited}"`,
        `""`,
        `"${t("item.meetingName")}: ${meetingTitle}"`,
        `""`,
        `"${t("details.invited")}: ${invitedList}"`,
        `"${t("details.viewers")}: ${distributionList}"`,
        `""`,
      ]
    : [
        `"Pinnacle";"${t("toolbar.export.exportDate")}: ${todayStr}"`,
        `"${fileSafeTitle}"`,
        `""`,
      ];

  const printHeader = isMeetingPage
    ? `
        <div style="display:flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px;">
            <div>
                <img src="${
                  window.location.origin
                }/icon.svg" style="height:8mm; margin-right:4px;">
                <img src="${
                  window.location.origin
                }/logo.svg" style="height:8mm;">
            </div>
                <br>
            <div style="text-align:right; font-weight:600; font-size:12pt;">
                ${t("meeting.date")}: ${meetingDate}<br>${t(
                  "details.last_edited",
                )}: ${lastEdited}
            </div>
        </div>
        <div style="margin:6mm 0 2mm 0; font-weight:600; font-size:12pt;">${meetingTitle}<br><br>${t(
          "details.invited",
        )}: ${invitedList}<br>${t("details.viewers")}: ${distributionList}</div>
        `
    : `
        <div style="margin-bottom:6mm;">
            <div style="display:flex; align-items:center; margin-bottom:4mm;">
                <img src="${
                  window.location.origin
                }/icon.svg" style="height:8mm; margin-right:4px;">
                <img src="${
                  window.location.origin
                }/logo.svg" style="height:8mm; margin-right:8px;">
            </div>
                <br>
            <div style="display:flex; align-items:center;">
                <div style="font-weight:600; font-size:12pt;">${fileSafeTitle}</div>
                <div style="margin-left:auto; font-weight:600; font-size:12pt;">${t(
                  "toolbar.export.exportDate",
                )}: ${todayStr}</div>
            </div>
        </div>
    `;

  return (
    <GridToolbarContainer sx={{ justifyContent: "flex-start" }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <Box sx={{ position: "relative", top: "-1px" }}>
        <CustomExport
          printOptions={{
            printHeader: printHeader,
            hiddenColumns: [t("item.risk"), t("meeting.actions"), null, "ID"],
          }}
          csvOptions={{
            customLines: customLines,
            exportFileName: exportFileName,
          }}
        />
      </Box>
      {props.showSettings && (
        <>
          <Button
            aria-describedby={id}
            startIcon={<SettingsOutlinedIcon />}
            onClick={handleClick}
          >
            {t("toolbar.settings")}
          </Button>

          <Popover
            id={id}
            open={open}
            onClose={handleClose}
            anchorEl={anchorEl}
            role={undefined}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <Paper>
              <Box sx={style}>
                <Typography id="modal-modal-title" variant="h6" component="h2">
                  {t("settings.title")}
                </Typography>
                <Divider />
                <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                  {t("settings.interval")}
                </Typography>
                <Box sx={{ width: 300, display: "inline" }}>
                  <Slider
                    aria-label="Always visible"
                    value={props.interval}
                    step={1}
                    min={1}
                    max={90}
                    marks={marks}
                    valueLabelDisplay="on"
                    onChange={changeProgressInterval}
                    color="secondary"
                    size="small"
                    sx={{ mt: 4 }}
                  />
                </Box>
              </Box>
            </Paper>
          </Popover>
        </>
      )}
      {/* Render toggle buttons inside the grid toolbar */}
      {!props.hideItemStatusToggles && (
        <ToolbarToggles
          onArchived={props.onArchived}
          onCompleted={props.onCompleted}
          onInactive={props.onInactive}
          onShowOnlyPrioritized={props.onShowOnlyPrioritized}
          onShowOnlySelfAssignedQuicktasks={
            props.onShowOnlySelfAssignedQuicktasks
          }
        />
      )}
    </GridToolbarContainer>
  );
};

export default CustomToolbar;
